import { VIEW_DETAILS_QUERY, isEmbedUrlAllowed, rawViewDetailsQueryValue, } from '../utils/permitDetailsEmbed';
/** Per-category display cap in the search dropdown. Both host containers scroll
 *  (`NavSearch`'s Popper and the mobile drawer), so this can safely be generous. */
const MAX_PER_BUCKET = 30;
function norm(s) {
    return s.trim().toLowerCase();
}
function includesI(hay, needle) {
    const n = needle.trim();
    if (!n)
        return false;
    return norm(hay).includes(norm(n));
}
/** Permits page for an agency row: `/permits/:dept?agency=:recordId`, or department-only when agency id missing. */
export function buildLandingAgencyPermitHref(a) {
    const dept = (a.departmentKey ?? '').trim();
    const agencyRecord = (a.recordId ?? '').trim();
    if (dept && agencyRecord) {
        const qs = new URLSearchParams({ agency: agencyRecord });
        return `/permits/${encodeURIComponent(dept)}?${qs.toString()}`;
    }
    if (dept)
        return `/permits/${encodeURIComponent(dept)}`;
    return null;
}
/**
 * SPA route that loads Flow Viewer in {@link PermitDetailsViewPage} iframe (`viewDetails`),
 * or department/agency list when no embed URL was resolved.
 */
export function buildLandingPermitCatalogHref(p) {
    const dept = p.departmentKey.trim();
    if (!dept)
        return null;
    const embed = p.detailsEmbedUrl?.trim();
    if (embed && isEmbedUrlAllowed(embed)) {
        const qs = new URLSearchParams();
        qs.set(VIEW_DETAILS_QUERY, rawViewDetailsQueryValue(embed));
        return `/permits/${encodeURIComponent(dept)}/details?${qs.toString()}`;
    }
    const agency = (p.agencyKey ?? '').trim();
    if (agency) {
        const qs = new URLSearchParams({ agency });
        return `/permits/${encodeURIComponent(dept)}?${qs.toString()}`;
    }
    return `/permits/${encodeURIComponent(dept)}`;
}
function buildDepartmentHref(d) {
    const h = d.href?.trim();
    if (h && h.startsWith('/'))
        return h;
    const rid = (d.recordId ?? '').trim();
    if (rid)
        return `/permits/${encodeURIComponent(rid)}`;
    return null;
}
function departmentHaystack(d) {
    const parts = [d.title, d.subtitle ?? ''];
    if (d.matchTitles?.length)
        parts.push(...d.matchTitles);
    return parts.join('\u0000');
}
function agencyNameHaystack(a) {
    return [a.name, a.subtitle ?? '', a.agencyGroup ?? ''].join('\u0000');
}
function agencyStableKey(a) {
    return `${(a.recordId ?? '').trim()}\0${(a.departmentKey ?? '').trim()}\0${a.name.trim()}`;
}
function permitCatalogHaystack(p) {
    return [
        p.title,
        p.description ?? '',
        p.departmentKey,
        p.agencyKey ?? '',
        p.scope ?? '',
        p.statewide ?? '',
        p.applyNow ?? '',
        p.applicationCost ?? '',
        p.processingTime ?? '',
        p.recordId,
    ].join('\u0000');
}
/** Append typed text to a catalog URL. Supports literal `{q}`; otherwise adds `?q=` or `&q=`. */
export function appendLandingSearchQueryToUrl(template, q) {
    const encoded = encodeURIComponent(q);
    if (template.includes('{q}'))
        return template.split('{q}').join(encoded);
    const sep = template.includes('?') ? '&' : '?';
    return `${template}${sep}q=${encoded}`;
}
/**
 * Client-side landing search: departments, agencies (names), and permit **titles** from the catalog grid
 * (plus optional external “full search” URL).
 */
export function computeLandingUnifiedSearch(rawQuery, departments, agencies, options) {
    const q = rawQuery.trim();
    const empty = { departments: [], agencies: [], permits: [] };
    if (!q)
        return empty;
    const departmentHits = [];
    const seenDeptHref = new Set();
    for (const d of departments) {
        if (!includesI(departmentHaystack(d), q))
            continue;
        const href = buildDepartmentHref(d);
        if (!href || seenDeptHref.has(href))
            continue;
        seenDeptHref.add(href);
        departmentHits.push({
            kind: 'department',
            id: `d:${href}`,
            title: d.title,
            subtitle: d.subtitle?.trim() || undefined,
            href,
        });
        if (departmentHits.length >= MAX_PER_BUCKET)
            break;
    }
    const nameMatchedKeys = new Set();
    const agencyHits = [];
    for (const a of agencies) {
        const href = buildLandingAgencyPermitHref(a);
        if (!href)
            continue;
        if (!includesI(agencyNameHaystack(a), q))
            continue;
        const sk = agencyStableKey(a);
        if (nameMatchedKeys.has(sk))
            continue;
        nameMatchedKeys.add(sk);
        agencyHits.push({
            kind: 'agency',
            id: `a:${sk}`,
            title: a.name,
            subtitle: (a.subtitle ?? a.agencyGroup)?.trim() || undefined,
            href,
        });
        if (agencyHits.length >= MAX_PER_BUCKET)
            break;
    }
    const permitHits = [];
    const tpl = options?.permitCatalogUrlTemplate?.trim();
    const catalogTitle = options?.permitCatalogTitle?.trim();
    if (tpl && catalogTitle) {
        permitHits.push({
            kind: 'permit',
            id: '__external_catalog__',
            title: catalogTitle,
            href: appendLandingSearchQueryToUrl(tpl, q),
        });
    }
    /** `p.departmentKey` / `p.agencyKey` are internal ids — resolve to display names for the result tags. */
    const departmentNameByKey = new Map();
    for (const d of departments) {
        const rid = (d.recordId ?? '').trim().toLowerCase();
        if (rid && !departmentNameByKey.has(rid))
            departmentNameByKey.set(rid, d.title);
    }
    const agencyNameByKey = new Map();
    for (const a of agencies) {
        const rid = (a.recordId ?? '').trim().toLowerCase();
        if (rid && !agencyNameByKey.has(rid))
            agencyNameByKey.set(rid, a.name);
    }
    const gridPermits = options?.permitsCatalog ?? [];
    const seenPermitKeys = new Set();
    for (const p of gridPermits) {
        if (permitHits.length >= MAX_PER_BUCKET)
            break;
        if (!includesI(permitCatalogHaystack(p), q))
            continue;
        const href = buildLandingPermitCatalogHref(p);
        if (!href)
            continue;
        const pk = `pc:${p.departmentKey}:${p.recordId}`;
        if (seenPermitKeys.has(pk))
            continue;
        seenPermitKeys.add(pk);
        const subtitleParts = [p.scope, p.applicationCost].filter((x) => (x ?? '').trim().length > 0);
        const subtitleFromMeta = subtitleParts.join(' · ');
        const subtitle = subtitleFromMeta ||
            (p.description && p.description.length > 0
                ? p.description.length > 120
                    ? `${p.description.slice(0, 117)}…`
                    : p.description
                : undefined);
        const departmentName = departmentNameByKey.get(p.departmentKey.trim().toLowerCase());
        const agencyKeyLc = (p.agencyKey ?? '').trim().toLowerCase();
        const agencyName = agencyKeyLc ? agencyNameByKey.get(agencyKeyLc) : undefined;
        permitHits.push({
            kind: 'permit',
            id: pk,
            title: p.title,
            subtitle,
            href,
            applyNow: p.applyNow?.trim() || undefined,
            departmentName,
            agencyName,
        });
    }
    return { departments: departmentHits, agencies: agencyHits, permits: permitHits };
}
